import type { FinixEnvironment } from '@/app/FinixEnvironmentContext';
import type { FirmData, MerchantAccount } from '@/database/baseTypes';

export const getRetainerTransferAccountFields = ({
  firm,
  finixEnvironment,
  matterUsState,
}: {
  firm: FirmData;
  finixEnvironment: FinixEnvironment;
  matterUsState: string;
}) => {
  const isSandbox = finixEnvironment === 'sandbox';
  const merchantAccounts = firm.merchantAccounts.filter(
    (account) => account.isSandbox === isSandbox,
  );
  const trustAccounts = merchantAccounts.filter(isTrustAccount);
  const stateAccounts = trustAccounts.filter(
    (account) => account.usState === matterUsState,
  );
  const defaultTrustId = isSandbox
    ? firm.defaultSandboxTrustAccountId
    : firm.defaultTrustAccountId;
  const statelessTrustAccounts = trustAccounts.filter(
    (account) => account.usState === undefined,
  );
  const fromInternalMerchantAccountId =
    stateAccounts.length === 1
      ? stateAccounts[0].id
      : defaultTrustId
        ? accountExists(trustAccounts, defaultTrustId, 'trust')
          ? defaultTrustId
          : undefined
        : statelessTrustAccounts.length === 1
          ? statelessTrustAccounts[0].id
          : undefined;
  const defaultOperatingId = isSandbox
    ? firm.defaultSandboxOperatingAccountId
    : firm.defaultOperatingAccountId;
  const toInternalMerchantAccountId = accountExists(
    merchantAccounts,
    defaultOperatingId,
    'operating',
  )
    ? defaultOperatingId
    : undefined;

  return {
    ...(fromInternalMerchantAccountId ? { fromInternalMerchantAccountId } : {}),
    ...(toInternalMerchantAccountId ? { toInternalMerchantAccountId } : {}),
  };
};

const accountExists = (
  accounts: MerchantAccount[],
  accountId: string | undefined,
  operatingOrTrust: MerchantAccount['operatingOrTrust'],
) =>
  !!accountId &&
  accounts.some(
    (account) =>
      account.id === accountId && account.operatingOrTrust === operatingOrTrust,
  );

const isTrustAccount = (account: MerchantAccount) =>
  account.operatingOrTrust === 'trust';
